import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { submitIntake } from '@/lib/api';
import { getTracking, clearTracking } from '@/lib/tracking';
import { ArrowLeft, ArrowRight, Zap, CheckCircle2, Loader2, Home, Building, Building2, Store, DoorOpen, ShieldCheck, Leaf, CircleDollarSign, Clock, Eye } from 'lucide-react';
import Logo from '@/components/Logo';
import OpeningsStep from '@/components/OpeningsStep';

const TOTAL_STEPS = 7;

// Track pixel event helper
const trackPixelEvent = (eventName, data = {}) => {
  if (window.fbq) {
    if (eventName === 'InitiateCheckout' || eventName === 'Lead') {
      window.fbq('track', eventName, data);
    } else {
      window.fbq('trackCustom', eventName, data);
    }
  }
};

export default function QuoteIntakeQuiz() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const verifiedToken = searchParams.get('verified') || '';
  const prefName = searchParams.get('name') || '';
  const prefPhone = searchParams.get('phone') || '';
  const isVerified = Boolean(verifiedToken);

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [uploading] = useState(false);

  // Quiz answers
  const [answers, setAnswers] = useState(() => {
    const saved = localStorage.getItem('snapWindowsQuizProgress');
    const base = saved ? JSON.parse(saved) : {
      propertyType: '',
      projectScope: '',
      openingCount: '',
      primaryGoal: '',
      timeline: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      zip: '',
      photos: [],
      uploadedFileIds: []
    };

    // If user came from /get-quote with a verified token, prefill name/phone
    if (prefName && !base.firstName) {
      const parts = prefName.trim().split(/\s+/);
      base.firstName = parts[0] || '';
      if (parts.length > 1 && !base.lastName) base.lastName = parts.slice(1).join(' ');
    }
    if (prefPhone && !base.phone) base.phone = prefPhone;

    return base;
  });

  // ---- Opening-based intake state (NEW) ----
  // Each opening: { id, file_id, preview, opening_type, width, height, notes,
  //                 validation, uploading }
  // Persisted partially (preview/file blobs are dropped because they can't be
  // serialized to localStorage — file_id survives so the lead still ships).
  const [openings, setOpenings] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('snapOpeningsV2') || '[]');
      return Array.isArray(saved) ? saved : [];
    } catch (_) { return []; }
  });
  const [overviews, setOverviews] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('snapOverviewsV2') || '[]');
      return Array.isArray(saved) ? saved : [];
    } catch (_) { return []; }
  });

  // Persist a SAFE (serializable) shape — drop preview blobs but keep file_ids
  useEffect(() => {
    try {
      const safe = openings.map(o => ({
        id: o.id,
        file_id: o.file_id || '',
        opening_type: o.opening_type || 'Window',
        width: o.width || '',
        height: o.height || '',
        notes: o.notes || '',
        validation: o.validation && !o.validation._fallback ? { status: o.validation.status, message: o.validation.message } : null,
      }));
      localStorage.setItem('snapOpeningsV2', JSON.stringify(safe));
    } catch (err) {
      console.warn('Failed to persist openings progress to localStorage:', err);
    }
  }, [openings]);
  useEffect(() => {
    try {
      const safe = overviews.map(o => ({ id: o.id, file_id: o.file_id || '' }));
      localStorage.setItem('snapOverviewsV2', JSON.stringify(safe));
    } catch (err) {
      console.warn('Failed to persist overview photos progress to localStorage:', err);
    }
  }, [overviews]);

  // Persist verified token (used by submit + analytics)
  useEffect(() => {
    if (verifiedToken) {
      try {
        localStorage.setItem(
          'snapVerified',
          JSON.stringify({ token: verifiedToken, name: prefName, phone: prefPhone, ts: Date.now() })
        );
      } catch (err) {
        console.warn('Failed to persist verified session to localStorage:', err);
      }
      // Track that a verified user landed in the quiz
      if (window.gtag) {
        window.gtag('event', 'quiz_start_verified', {
          event_category: 'quiz_funnel',
          event_label: 'Verified Lead Started Quiz',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifiedToken]);

  // Save progress to localStorage
  useEffect(() => {
    localStorage.setItem('snapWindowsQuizProgress', JSON.stringify(answers));
  }, [answers]);

  // Track quiz start
  useEffect(() => {
    if (step === 0) {
      trackPixelEvent('InitiateCheckout');
      if (window.gtag) {
        window.gtag('event', 'quiz_start', {
          event_category: 'engagement',
          event_label: 'Quote Quiz Started'
        });
        window.gtag('event', 'quiz_step_1', {
          event_category: 'quiz_funnel',
          event_label: 'Property Type'
        });
      }
    }
    // Intentionally run ONCE on mount only (fires the "quiz started" event a
    // single time). Adding `step` here would re-fire this on every step
    // change/back-navigation to step 0, double-counting the funnel event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track step changes
  useEffect(() => {
    if (step > 0 && window.gtag) {
      window.gtag('event', `quiz_step_${step + 1}`, {
        event_category: 'quiz_funnel',
        event_label: getStepLabel(step)
      });
    }
  }, [step]);

  const getStepLabel = (stepNum) => {
    const labels = [
      'Property Type',
      'Replacement Type', 
      'Opening Count',
      'Primary Goal',
      'Timeline',
      'Contact Info',
      'Photos of Openings'
    ];
    return labels[stepNum] || 'Unknown Step';
  };

  // Get recommended photos + minimum threshold based on property type.
  // Recommendations guide the user; minimums let them continue with fewer
  // photos if some angles are inaccessible.
  const progress = ((step + 1) / TOTAL_STEPS) * 100;

  const updateAnswer = (field, value) => {
    setAnswers(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = async () => {
    // Track step completion
    trackPixelEvent('QuoteStepCompleted', { step: step + 1 });
    setStep(prev => Math.min(prev + 1, TOTAL_STEPS - 1));
  };

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 0));
  };

  const handleSelectAndNext = (field, value) => {
    updateAnswer(field, value);
    setTimeout(() => {
      trackPixelEvent('QuoteStepCompleted', { step: step + 1 });
      setStep(prev => prev + 1);
    }, 150);
  };


  const handleSubmit = async () => {
    // Opening-based gate: require ≥1 opening with usable photo.
    const readyOpenings = openings.filter(o => !!o.file_id && o.validation?.status !== 'reject');
    if (readyOpenings.length < 1) {
      toast.error('Please add at least one opening photo to continue.');
      return;
    }

    if (!answers.firstName || !answers.lastName || !answers.email || !answers.phone || !answers.address) {
      toast.error('Please fill in all required fields including property address');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(answers.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSubmitting(true);
    try {
      const tracking = getTracking();

      // Build opening payload (new primary path) + flat file_ids for legacy compat
      const openingsPayload = readyOpenings.map((o, idx) => ({
        room_name: '',
        opening_type: o.opening_type || 'Window',
        width: o.width ? parseFloat(o.width) || null : null,
        height: o.height ? parseFloat(o.height) || null : null,
        width_unit: 'in',
        quantity: 1,
        notes: (o.notes || '').trim(),
        file_id: o.file_id,
      }));

      const overviewFileIds = (overviews || [])
        .filter(o => !!o.file_id)
        .map(o => o.file_id);

      // Combined file_ids preserves backward compatibility for any legacy
      // GHL workflow mapping that still reads `photo_url_1..4`.
      const combinedFileIds = [
        ...readyOpenings.map(o => o.file_id),
        ...overviewFileIds,
      ];

      // Notes: keep existing project context line. Backend appends opening
      // details + overview photo URLs to this before shipping to GHL.
      const baseNotes = `Project Scope: ${answers.projectScope}. Primary Goal: ${answers.primaryGoal}. Opening Count: ${answers.openingCount}.`;

      const data = {
        first_name: answers.firstName,
        last_name: answers.lastName,
        email: answers.email,
        phone: answers.phone,
        property_address: answers.address,
        city: answers.city || 'Miami',
        county: '',
        zip_code: answers.zip || '33139',
        property_type: answers.propertyType,
        project_type: 'Replacement',
        // Opening-based intake — derive coarse counts for legacy scoring/tags
        num_windows: openingsPayload.filter(o => /window|fixed glass/i.test(o.opening_type)).length,
        num_doors: openingsPayload.filter(o => /door/i.test(o.opening_type)).length,
        full_house: answers.openingCount === '10+',
        hurricane_protection: answers.primaryGoal === 'Hurricane protection' ? 'No' : 'Not sure',
        hoa: 'Not sure',
        financing_interest: 'Not sure',
        desired_timeframe: answers.timeline,
        budget_range: '',
        notes: baseNotes,
        // NEW primary path
        openings: openingsPayload,
        overview_file_ids: overviewFileIds,
        // Legacy combined ids (backend de-dupes)
        file_ids: combinedFileIds,
        photo_metadata: {
          opening_count_actual: openingsPayload.length,
          overview_count: overviewFileIds.length,
          flow_version: 'opening_v2',
        },
        // Tracking + attribution
        utm_source: tracking.utm_source,
        utm_medium: tracking.utm_medium,
        utm_campaign: tracking.utm_campaign,
        utm_content: tracking.utm_content,
        utm_term: tracking.utm_term,
        landing_page: tracking.landing_page,
        referrer: tracking.referrer,
        src: tracking.src || searchParams.get('src') || '',
        verified_phone: isVerified,
        verify_token: verifiedToken || '',
        consent: true,
      };

      const resp = await submitIntake(data);

      // Track lead conversion
      trackPixelEvent('Lead');
      if (window.gtag) {
        window.gtag('event', 'generate_lead', {
          event_category: 'conversion',
          event_label: 'Quote Request Submitted',
          value: 1,
        });
        window.gtag('event', 'quiz_complete', {
          event_category: 'quiz_funnel',
          event_label: 'Quiz Fully Completed',
          property_type: answers.propertyType,
          timeline: answers.timeline,
          openings_count: openingsPayload.length,
          overview_count: overviewFileIds.length,
        });
      }

      // NOTE: GoHighLevel webhook is fired exclusively from the backend
      // (POST /api/intake/submit -> send_to_ghl_webhook). Do NOT call GHL
      // directly from the browser — it would create duplicate leads.

      // Clear localStorage + tracking attribution after successful submit
      localStorage.removeItem('snapWindowsQuizProgress');
      localStorage.removeItem('snapOpeningsV2');
      localStorage.removeItem('snapOverviewsV2');
      clearTracking();

      navigate(`/status/${resp.data.public_token}`);
    } catch (err) {
      toast.error('Submission failed. Please try again.');
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    switch(step) {
      case 0: return answers.propertyType;
      case 1: return answers.projectScope;
      case 2: return answers.openingCount;
      case 3: return answers.primaryGoal;
      case 4: return answers.timeline;
      case 5: return answers.firstName && answers.lastName && answers.email && answers.phone && answers.address;
      case 6: {
        // Opening-based gate: require at least one opening with a usable photo
        // (uploaded file_id present and AI validation status is not 'reject').
        const ready = openings.filter(o => !!o.file_id && o.validation?.status !== 'reject');
        return ready.length >= 1;
      }
      default: return false;
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-[hsl(var(--border))]">
        <div className="mx-auto w-full max-w-2xl px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <Logo variant="light" size="small" />
            <span className="text-xs text-[var(--brand-slate-600)] font-medium">Step {step + 1} of {TOTAL_STEPS}</span>
          </div>
          <Progress value={progress} className="h-2" data-testid="quiz-progress-bar" />
          <div className="flex justify-center gap-2 mt-3">
            <Badge variant="outline" className="text-[10px] px-2 py-0.5 border-[var(--brand-ocean-600)] text-[var(--brand-ocean-600)]">
              <Clock className="h-3 w-3 mr-1" /> Most quotes delivered within 24 hours
            </Badge>
            <Badge variant="outline" className="text-[10px] px-2 py-0.5 border-[var(--brand-ocean-600)] text-[var(--brand-ocean-600)]">
              No appointment required • No pressure
            </Badge>
          </div>
        </div>
      </div>

      {/* Quiz Content */}
      <div className="mx-auto w-full max-w-2xl px-4 py-8 pb-28">
        {/* Step 0: Property Type */}
        {step === 0 && (
          <div className="space-y-6 animate-fadeIn">
            <div className="text-center mb-8">
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[var(--brand-slate-900)] mb-3">Tell us about your property</h2>
              <p className="text-sm text-[var(--brand-slate-600)]">This helps us match the right products and pricing.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { value: 'House', icon: Home, label: 'House' },
                { value: 'Condo', icon: Building, label: 'Condo' },
                { value: 'Townhome', icon: Building2, label: 'Townhome' },
                { value: 'Commercial', icon: Store, label: 'Commercial' }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSelectAndNext('propertyType', option.value)}
                  className={`relative p-6 rounded-2xl border-2 transition-all duration-200 ${
                    answers.propertyType === option.value
                      ? 'border-[var(--brand-ocean-600)] bg-[var(--brand-ocean-100)]/20'
                      : 'border-[hsl(var(--border))] hover:border-[var(--brand-ocean-600)] hover:bg-[hsl(var(--secondary))]/50'
                  }`}
                  data-testid={`quiz-property-type-${option.value.toLowerCase()}`}
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-14 h-14 rounded-xl bg-[var(--brand-ocean-100)] flex items-center justify-center">
                      <option.icon className="h-7 w-7 text-[var(--brand-ocean-700)]" />
                    </div>
                    <span className="font-heading font-semibold text-lg text-[var(--brand-slate-900)]">{option.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Project Scope */}
        {step === 1 && (
          <div className="space-y-6 animate-fadeIn">
            <div className="text-center mb-8">
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[var(--brand-slate-900)] mb-3">What are you looking to replace?</h2>
              <p className="text-sm text-[var(--brand-slate-600)]">Most homes include both — we'll guide you either way.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { value: 'Windows', icon: Home, label: 'Windows Only' },
                { value: 'Doors', icon: DoorOpen, label: 'Doors Only' },
                { value: 'Both', icon: Building, label: 'Windows & Doors' },
                { value: 'Not sure', icon: CheckCircle2, label: 'Not Sure Yet' }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSelectAndNext('projectScope', option.value)}
                  className={`relative p-6 rounded-2xl border-2 transition-all duration-200 ${
                    answers.projectScope === option.value
                      ? 'border-[var(--brand-ocean-600)] bg-[var(--brand-ocean-100)]/20'
                      : 'border-[hsl(var(--border))] hover:border-[var(--brand-ocean-600)] hover:bg-[hsl(var(--secondary))]/50'
                  }`}
                  data-testid={`quiz-project-scope-${option.value.toLowerCase().replace(' ', '-')}`}
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-14 h-14 rounded-xl bg-[var(--brand-ocean-100)] flex items-center justify-center">
                      <option.icon className="h-7 w-7 text-[var(--brand-ocean-700)]" />
                    </div>
                    <span className="font-heading font-semibold text-lg text-[var(--brand-slate-900)]">{option.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Opening Count */}
        {step === 2 && (
          <div className="space-y-6 animate-fadeIn">
            <div className="text-center mb-8">
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[var(--brand-slate-900)] mb-3">About how many windows/doors are we looking at?</h2>
              <p className="text-sm text-[var(--brand-slate-600)]">A rough estimate is perfectly fine.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { value: '1-5', label: '1-5 Openings', desc: 'Small project' },
                { value: '6-10', label: '6-10 Openings', desc: 'Medium project' },
                { value: '10+', label: '10+ Openings', desc: 'Large project' },
                { value: 'Not sure', label: 'Not Sure', desc: "We'll help count" }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSelectAndNext('openingCount', option.value)}
                  className={`relative p-6 rounded-2xl border-2 transition-all duration-200 ${
                    answers.openingCount === option.value
                      ? 'border-[var(--brand-ocean-600)] bg-[var(--brand-ocean-100)]/20'
                      : 'border-[hsl(var(--border))] hover:border-[var(--brand-ocean-600)] hover:bg-[hsl(var(--secondary))]/50'
                  }`}
                  data-testid={`quiz-opening-count-${option.value.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                >
                  <div className="flex flex-col items-center gap-2 text-center">
                    <span className="font-heading font-semibold text-xl text-[var(--brand-slate-900)]">{option.label}</span>
                    <span className="text-xs text-[var(--brand-slate-600)]">{option.desc}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Primary Goal */}
        {step === 3 && (
          <div className="space-y-6 animate-fadeIn">
            <div className="text-center mb-8">
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[var(--brand-slate-900)] mb-3">What's most important to you?</h2>
              <p className="text-sm text-[var(--brand-slate-600)]">Select your top priority</p>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {[
                { value: 'Lower my insurance', icon: CircleDollarSign, label: 'Lower my insurance', desc: 'Reduce premiums' },
                { value: 'Hurricane protection', icon: ShieldCheck, label: 'Hurricane protection', desc: 'Storm safety' },
                { value: 'Improve energy efficiency', icon: Leaf, label: 'Improve energy efficiency', desc: 'Lower bills' },
                { value: 'Upgrade the look of my home', icon: Home, label: 'Upgrade the look of my home', desc: 'Curb appeal' },
                { value: 'Just exploring', icon: Eye, label: 'Just exploring', desc: 'Gathering info' }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSelectAndNext('primaryGoal', option.value)}
                  className={`relative p-5 rounded-2xl border-2 transition-all duration-200 ${
                    answers.primaryGoal === option.value
                      ? 'border-[var(--brand-ocean-600)] bg-[var(--brand-ocean-100)]/20'
                      : 'border-[hsl(var(--border))] hover:border-[var(--brand-ocean-600)] hover:bg-[hsl(var(--secondary))]/50'
                  }`}
                  data-testid={`quiz-primary-goal-${option.value.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[var(--brand-ocean-100)] flex items-center justify-center shrink-0">
                      <option.icon className="h-6 w-6 text-[var(--brand-ocean-700)]" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-heading font-semibold text-base text-[var(--brand-slate-900)]">{option.label}</p>
                      <p className="text-xs text-[var(--brand-slate-600)] mt-0.5">{option.desc}</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-[var(--brand-ocean-600)]" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Timeline */}
        {step === 4 && (
          <div className="space-y-6 animate-fadeIn">
            <div className="text-center mb-8">
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[var(--brand-slate-900)] mb-3">When are you planning to move forward?</h2>
              <p className="text-sm text-[var(--brand-slate-600)]">(This helps us prioritize your quote)</p>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {[
                { value: 'ASAP', label: 'As Soon As Possible', desc: 'Ready to move forward' },
                { value: '1-3 months', label: '1–3 Months', desc: 'Planning ahead' },
                { value: 'Just exploring', label: 'Just Exploring', desc: 'Gathering information' }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSelectAndNext('timeline', option.value)}
                  className={`relative p-6 rounded-2xl border-2 transition-all duration-200 ${
                    answers.timeline === option.value
                      ? 'border-[var(--brand-ocean-600)] bg-[var(--brand-ocean-100)]/20'
                      : 'border-[hsl(var(--border))] hover:border-[var(--brand-ocean-600)] hover:bg-[hsl(var(--secondary))]/50'
                  }`}
                  data-testid={`quiz-timeline-${option.value.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <p className="font-heading font-semibold text-xl text-[var(--brand-slate-900)]">{option.label}</p>
                      <p className="text-sm text-[var(--brand-slate-600)] mt-0.5">{option.desc}</p>
                    </div>
                    <ArrowRight className="h-6 w-6 text-[var(--brand-ocean-600)]" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 5: Contact Info */}
        {step === 5 && (
          <div className="space-y-6 animate-fadeIn">
            <div className="text-center mb-8">
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[var(--brand-slate-900)] mb-3">Where should we send your quote?</h2>
              <p className="text-sm text-[var(--brand-slate-600)]">We'll email your pricing within 24 hours</p>
              {isVerified && (
                <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-green-50 border border-green-200 px-3 py-1.5 text-xs font-medium text-green-700" data-testid="quiz-verified-badge">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Phone verified — just confirm your details below
                </div>
              )}
            </div>
            <Card className="rounded-2xl border-2">
              <CardContent className="p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      value={answers.firstName}
                      onChange={e => updateAnswer('firstName', e.target.value)}
                      placeholder="John"
                      className="mt-1.5 h-12"
                      data-testid="quiz-contact-first-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      value={answers.lastName}
                      onChange={e => updateAnswer('lastName', e.target.value)}
                      placeholder="Smith"
                      className="mt-1.5 h-12"
                      data-testid="quiz-contact-last-name"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={answers.email}
                    onChange={e => updateAnswer('email', e.target.value)}
                    placeholder="john@example.com"
                    className="mt-1.5 h-12"
                    data-testid="quiz-contact-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={answers.phone}
                    onChange={e => updateAnswer('phone', e.target.value)}
                    placeholder="(305) 555-1234"
                    className="mt-1.5 h-12"
                    data-testid="quiz-contact-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="address">Property Address *</Label>
                  <Input
                    id="address"
                    value={answers.address}
                    onChange={e => {
                      updateAnswer('address', e.target.value);
                      // Track address completion when field has a value
                      if (e.target.value && e.target.value.length > 5 && !answers.address) {
                        if (window.gtag) {
                          window.gtag('event', 'address_completed', {
                            event_category: 'quiz_engagement',
                            event_label: 'Address Field Completed'
                          });
                        }
                      }
                    }}
                    onFocus={() => {
                      // Track address field focus
                      if (window.gtag) {
                        window.gtag('event', 'address_field_focused', {
                          event_category: 'quiz_engagement',
                          event_label: 'Address Field Focused'
                        });
                      }
                    }}
                    placeholder="123 Ocean Drive"
                    className="mt-1.5 h-12"
                    data-testid="quiz-contact-address"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City (Optional)</Label>
                    <Input
                      id="city"
                      value={answers.city}
                      onChange={e => updateAnswer('city', e.target.value)}
                      placeholder="Miami"
                      className="mt-1.5 h-12"
                    />
                  </div>
                  <div>
                    <Label htmlFor="zip">ZIP (Optional)</Label>
                    <Input
                      id="zip"
                      value={answers.zip}
                      onChange={e => updateAnswer('zip', e.target.value)}
                      placeholder="33139"
                      className="mt-1.5 h-12"
                    />
                  </div>
                </div>
                <div className="bg-[var(--brand-ocean-100)]/20 rounded-xl p-4 mt-4 space-y-2">
                  <p className="text-xs text-[var(--brand-slate-700)] flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)] shrink-0 mt-0.5" />
                    <span>We use your address to calculate accurate pricing — no in-home visit or surprise appointments.</span>
                  </p>
                  <p className="text-xs text-[var(--brand-slate-700)] flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)] shrink-0 mt-0.5" />
                    <span>We'll send your quote within 24 hours — no calls unless you request it.</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 6: Opening-based Photo Upload (replaces rigid 2/4 property-side flow) */}
        {step === 6 && (
          <OpeningsStep
            openings={openings}
            setOpenings={setOpenings}
            overviews={overviews}
            setOverviews={setOverviews}
          />
        )}
      </div>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 border-t bg-white/95 backdrop-blur px-4 py-3 shadow-[0_-4px_20px_rgba(15,23,32,0.08)]">
        <div className="mx-auto w-full max-w-2xl flex gap-3">
          {step > 0 && (
            <Button variant="outline" onClick={handleBack} className="rounded-xl h-12 px-5 border-2" data-testid="quiz-back-button">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
          )}
          {step === 0 && (
            <Button variant="ghost" onClick={() => navigate(isVerified ? '/get-quote' : '/')} className="rounded-xl h-12 px-5">
              <ArrowLeft className="h-4 w-4 mr-2" /> {isVerified ? 'Back' : 'Home'}
            </Button>
          )}
          {step < 6 && (
            <Button
              onClick={handleNext}
              disabled={!canProceed() || uploading}
              className="flex-1 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))] rounded-xl h-12 font-semibold transition-all duration-200 shadow-sm disabled:opacity-50"
              data-testid="quiz-next-button"
            >
              {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</> : <>Next <ArrowRight className="h-4 w-4 ml-2" /></>}
            </Button>
          )}
          {step === 6 && (
            <Button
              onClick={handleSubmit}
              disabled={submitting || !canProceed()}
              className="flex-1 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))] rounded-xl h-12 font-semibold transition-all duration-200 shadow-sm disabled:opacity-50"
              data-testid="quiz-submit-button"
            >
              {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...</> : <>Get My Pricing Options (24hr turnaround) <Zap className="h-4 w-4 ml-2" /></>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
